import { themeZafire } from '@zafire/ag-grid-enterprise';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import type { LiquidityOverviewDto, LiquidityPositionDto } from '../../../app/api/generated/client';
import { AgGridTable } from '../../../app/components/atoms';
import { PRIMARY_NAVIGATION_BAR_HEIGHT, WEALTH_ASSET_BANKABLE_CATEGORY_ID } from '../../../app/config/constants';
import { useLayout } from '../../../app/contexts/layout-context';
import { useYwuApp } from '../../../app/contexts/ywu-app-context';
import type { IModalBalanceEntry } from '../../../app/interfaces/balance-sheet-table';
import type { LiquidityOverviewTableRow } from '../../interfaces';
import { buildModalBalanceEntryFromLiquidityRow } from '../../utils';
import { useLiquidityOverviewTableColDefs } from './use-liquidity-overview-table-col-defs';

// Liquidity-planner header (154) + margin & padding content-box (48 + 48) + gap table/chart (16) + bar chart + external legend (~105)
const LIQUIDITY_OVERVIEW_VIEWPORT_RESERVE_BELOW_NAVBAR_PX = 154 + 48 + 48 + 16 + 105;

interface ILiquidityOverviewTableProps {
    overviewData?: LiquidityOverviewDto;
    setModalEntry: (entry?: IModalBalanceEntry) => void;
}

export const LiquidityOverviewTable: FunctionComponent<ILiquidityOverviewTableProps> = ({ overviewData, setModalEntry }) => {
    const { totalNavbarHeight } = useLayout();
    const { isWealthNavigatorMFE } = useYwuApp();
    const liquidityOverviewTableColDefs = useLiquidityOverviewTableColDefs();

    const onLiquidityRowAction = useCallback(
        (row: LiquidityPositionDto) =>
            setModalEntry({
                categoryId: row.categoryId ?? WEALTH_ASSET_BANKABLE_CATEGORY_ID,
                balanceEntry: buildModalBalanceEntryFromLiquidityRow(row),
            }),
        [setModalEntry],
    );

    const rowData = useMemo<LiquidityOverviewTableRow[]>(() => {
        const assets = overviewData?.positions ?? [];

        return assets.map((row) => ({ ...row, onLiquidityRowAction }));
    }, [overviewData, onLiquidityRowAction]);

    const navHeight = isWealthNavigatorMFE ? PRIMARY_NAVIGATION_BAR_HEIGHT : totalNavbarHeight;
    const screenHeaderPaddingsAndHeight = navHeight + LIQUIDITY_OVERVIEW_VIEWPORT_RESERVE_BELOW_NAVBAR_PX;

    return (
        <AgGridTable<LiquidityOverviewTableRow>
            addTableContainer
            theme={themeZafire.withZafire('density', 'comfort').withParams({ wrapperBorder: false })}
            rowData={rowData}
            columnDefs={liquidityOverviewTableColDefs}
            defaultColDef={{
                resizable: false,
                cellStyle: { display: 'flex', alignItems: 'center' },
            }}
            screenHeaderPaddingsAndHeight={screenHeaderPaddingsAndHeight}
            headerRowHeight={45}
            tableRowHeight={45}
            tableContainerPadding={0}
            tableHeightSlackPx={2}
        />
    );
};
